import { cupisPairPass, type CupisPairMode } from './cupisPairs';
import { Decimal } from 'decimal.js';
import { transferEligible, transferFloor, transferRoi } from '../lib/transferFeed';
import { RU_MIN_ROI } from './gate';
import { EXPERIMENTS, PRIVATE_MIRROR, feedKey, isMainFeed2Id, isCupisForkId, PREDICT_FEED, RU_FEED, TRANSFERS } from '../lib/feedContext';
import { isLenta5Id } from '../lib/lenta5';
import { canonTapes, tapeOfFork } from './tapes';
/**
 * Что человек хочет видеть в ленте и в каком порядке.
 *
 * Первое, что ищет в чужом сканере опытный вилочник, — список контор. Причина не
 * в привередливости: у него порезаны лимиты в одних конторах, нет счёта в
 * других, а третьи он не берёт принципиально. Строка с такой ногой для него не
 * «менее интересна», её просто нет — но глазами он её всё равно читает, и это
 * стоит секунд на каждой.
 *
 * Устройство фильтров подчинено одному правилу: **умолчание остаётся одной
 * ручкой**. Порог «беру от ₽» — единственное, что видно новичку; всё остальное
 * свёрнуто и не мешает. Сканер, встречающий человека двадцатью полями, теряет
 * его раньше, чем покажет первую вилку.
 *
 * Список счетов читается двумя способами, и это не дублирование. «Только эти» —
 * режим человека с тремя счетами: он перечислит их один раз и забудет. «Все,
 * кроме» — режим того, у кого счета почти везде и порезаны две конторы. Свести
 * их в один список нельзя: при появлении новой конторы белый список её скроет,
 * а чёрный покажет, и оба поведения правильные — для разных людей.
 *
 * «Главная» — другой вопрос, не третье чтение тех же фишек. Откуда работаю:
 * в вилке обязана стоять хотя бы одна из `mainVenues`. Счета при этом живут
 * своей полкой. Несколько главных — ИЛИ: полик обязателен значит вилки с ногой
 * Polymarket, вторые ноги режет уже «только / кроме». Главная автоматически
 * своя в белом списке: иначе «главная Poly + только Thunder» давала бы пустую
 * ленту. Умолчание пустое: иначе одна площадка «по умолчанию» снова опустошит
 * ленту тем, у кого её нет.
 *
 * Фильтр не удаляет строки молча. Сколько он скрыл, лента обязана назвать
 * числом: молчаливая пропажа неотличима от «вилок нет», и человек уходит
 * искать их туда, где обещают миллион.
 */

import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import { MIRROR_FEED } from '../lib/build';
import { sportCanon } from '../lib/sports';
import { UNNAMED_VENUE, type Fork } from '../lib/protocol';
import { isMirrorBook } from './Books';
import { bookOf, feedPairOk, foldKey, keepTwin, ruPairOk, sportOf } from './mirrorDesk';
import { sieveDropsBook } from './venues';
import { type MarketMode, marketPasses, marketTags, migrateMarkets } from '../lib/markets';
import { useAccounts, type Account, type VenueExclusion } from './accounts';
import { rememberPlaces, settle } from './calm';
import { useClipPrune, useClips } from './clip';
import { applyProfilePatch, holds, type Profile, useProfile } from './profile';
import { profilePatchFromSieve } from './studio/sync';
import { type PageLine, isDry, pageLine } from './route';
import { distrust, type FeedRow, type FeedView, useRows } from './rows';
import { heldIds, useScreen } from './store';

/** Как читать список счетов. Главная живёт отдельным полем, не третьим режимом. */
export type VenueMode = 'only' | 'except';

/** Общий пустой набор: новый `Set` на каждый вызов ломал бы мемоизацию сита. */
const EMPTY: ReadonlySet<string> = new Set();

const isPreparedFeed2 = (row: FeedRow): boolean => isMainFeed2Id(row.id);

/**
 * Отпечаток события строки. Считает его СЕРВЕР (`ev`): отпечаток — результат
 * матчинга с нормализацией имён, и вторая его реализация на клиенте разошлась
 * бы с первой молча, разложив один матч по двум группам. Строка без отпечатка
 * отвечает за себя сама — по своему `id`, и ни с кем не сворачивается.
 */
/** На /app вход в сите; на /russkie — тоже. На переливах и основной v2-ленте нет. */
export const ENTRY_SIEVE = !TRANSFERS && (!MIRROR_FEED || RU_FEED);

const keyOf = (row: FeedRow): string => {
  if (RU_FEED) {
    const fold = foldKey(row.row.fork);
    if (fold !== '') return fold;
  }
  if (typeof row.row.fork.ev === 'string' && row.row.fork.ev !== '') return row.row.fork.ev;
  return row.id;
};

export function entryOpen(leg: { precision?: string; url?: string | null }): boolean {
  const url = (leg.url ?? '').trim();
  if (url === '' || /undefined|\bnull\b|\bnan\b/i.test(url)) return false;
  const precision = leg.precision ?? '';
  return precision === 'event' || precision === 'outcome' || precision === 'betslip';
}

/**
 * По чему сортировать ленту. Умолчание — деньги, и менять его нельзя.
 *
 * «По проценту» снято 23.08: на живой ленте этот порядок неотличим от денег
 * (владелец), а старый замер 02.08 как раз запрещал ставить процент наверх —
 * там оказывалось то, что брать нельзя. Сохранённый `roi` читается как деньги.
 */
export type SortKey = 'money' | 'age' | 'life';

export const SORT_NAMES: Record<SortKey, string> = {
  money: TRANSFERS ? 'по удобству' : 'по деньгам',
  age: 'по свежести',
  life: 'по живучести',
};

export const asSort = (value: unknown): SortKey =>
  typeof value === 'string' && value in SORT_NAMES ? (value as SortKey) : 'money';

export interface Filters {
  cupisFlow?: 'all' | 'arbs' | 'transfers';
  cupisPairMode?: CupisPairMode;
  cupisPairs?: string[];
  /** Коды площадок. Пусто — фильтра нет вовсе, а не «ни одной». */
  venues: string[];
  venueMode: VenueMode;
  /**
   * Должна стоять на ноге. Пусто — не режем. Несколько — хотя бы одна.
   * Не счета: белый и чёрный список живут в `venues`.
   */
  mainVenues: string[];
  /** Коды дисциплин. Пусто — все. */
  sports: string[];
  /** Семейства рынка (`MarketFamily`). Пусто — все. */
  markets: string[];
  /** Как читать отметки рынков: «только эти» или «все, кроме». */
  marketMode?: MarketMode;
  /** `live` и `prematch`. Пусто — обе. */
  lines: string[];
  /**
   * Потоки кадра: `0` наши адаптеры, `1` Кибер ГГ, `2` BetBurger, `5` publication v5.
   * Пусто — все сразу. Несколько — только они.
   */
  tapes: string[];
  /** Границы процента. `null` — граница не поставлена. */
  roiMin: number | null;
  roiMax: number | null;
  /** Предельный возраст строки, секунды. */
  ageMax: number | null;
  /** Свежесть: лайв-строка моложе этого порога, секунды, скрыта. */
  ageMin: number | null;
  sort: SortKey;
  /** Раскрыта ли панель. Свёрнута по умолчанию — см. шапка файла. */
  open: boolean;
  /**
   * Только строки, куда есть вход: хотя бы одна нога ведёт на матч или исход.
   * Профиль «работа» с `prec: any` не трогаем — тумблер сита, не дефолт студии.
   * Новая сессия включает тумблер: музей без входа — не товар.
   */
  entryOnly: boolean;
  /** Дополнительные фильтры только для страницы «Переливы». */
  transferFull?: boolean;
  transferRules?: boolean;
  transferBook?: boolean;
}

export const EMPTY_FILTERS: Filters = {
  cupisFlow: RU_FEED ? 'all' : 'arbs', cupisPairMode: 'all', cupisPairs: [],
  venues: [],
  venueMode: 'except',
  mainVenues: [],
  sports: [],
  markets: [],
  marketMode: 'except',
  lines: [],
  tapes: [],
  roiMin: null,
  roiMax: null,
  ageMax: null,
  ageMin: null,
  sort: 'money',
  open: false,
  // Пустой объект сита в тестах и «сбросить остальное» — без этого флага.
  // Живая сессия включает его отдельно: см. `entryOnlyFromSaved`.
  entryOnly: false,
  transferFull: false,
  transferRules: false,
  transferBook: false,
};

/**
 * «Только вход» больше не стоит по умолчанию. Явный `true` — человек включил сам.
 *
 * Тумблер требует, чтобы хотя бы у одной ноги адрес вёл на матч или исход, и
 * заводился он против «очереди в никуда»: искать событие руками на разделе
 * дольше, чем живёт вилка. Довод верен для строки, у которой ОБЕ ноги без
 * адреса, но правило било по всем — а адрес есть не у всех площадок вовсе, и
 * реальность вилки не в адресе (в профиле это уже сказано: `link: 'no'`).
 *
 * Владелец 20.08 после пустого экрана: «сделай так, чтобы вход не требовался,
 * даже если 0 ссылок — тоже заебись». Тумблер остаётся на панели, положение
 * заводское поменялось.
 */
export function entryOnlyFromSaved(saved: { entryOnly?: unknown }): boolean {
  return saved.entryOnly === true;
}

/**
 * Разовый перевод сохранённой вкладки. Менять заводское значение мало: вкладка
 * сканера живёт неделями, `entryOnly: true` в ней уже записан, и человек увидел
 * бы правку только после ручной чистки хранилища.
 *
 * Метка ставится один раз, поэтому включённый заново тумблер держится.
 */
const ENTRY_FREE_KEY = feedKey('forq.next.filters.entry-free');

export function entryOnlyOnStart(saved: { entryOnly?: unknown }): boolean {
  try {
    if (localStorage.getItem(ENTRY_FREE_KEY) !== '1') {
      localStorage.setItem(ENTRY_FREE_KEY, '1');
      return false;
    }
  } catch {
    // Приватный режим — не повод падать.
  }
  return entryOnlyFromSaved(saved);
}

/*
 * Мягкого режима больше нет — 12.08, владелец: «бестолковый, убрать».
 *
 * Была кнопка «мягкие» рядом со звуком: она ставила полосу 0.5–1.5 % одним
 * нажатием, и рядом с ней стояло объяснение из нашей истории. Объяснение было
 * честным, а кнопка — нет: она выдавала за способ работы одну частную границу
 * процента. Границу ставят однажды и держат профилем, а не переключают посреди
 * ленты, и человек за рабочим экраном такой вопрос себе не задаёт. Зато цена
 * случайного нажатия была самой высокой на всей панели: верх ленты пустел, и
 * это читалось как мёртвый рынок.
 *
 * Сами границы процента (`roiMin`, `roiMax`) на месте: их ставит студия, сито
 * умеет их назвать и снять. Убран режим, а не настройка.
 */

/** Стоит ли на фильтрах хоть что-нибудь. Порядок сортировки фильтром не считается. */
export const anyFilter = (f: Filters): boolean =>
  f.venues.length > 0 ||
  f.mainVenues.length > 0 ||
  f.sports.length > 0 ||
  f.markets.length > 0 ||
  f.lines.length > 0 ||
  f.tapes.length > 0 ||
  f.roiMin !== null ||
  f.roiMax !== null ||
  f.ageMax !== null ||
  f.ageMin !== null ||
  f.entryOnly ||
  (TRANSFERS && (f.transferFull === true || f.transferRules === true || f.transferBook === true));

// ── Семейство рынка ──────────────────────────────────────────────────────────

/*
 * Само семейство считается в `lib/protocol`: тем же вопросом «на что ставим»
 * задаётся профиль (`profile.holds`), а профиль лежит НИЖЕ сита — сито его
 * читает. Оставь функцию здесь, и профилю пришлось бы импортировать сито,
 * которое импортирует профиль. Имена остаются доступны отсюда: половина
 * экранов знает их по этому адресу.
 */
export { FAMILY_NAMES, marketFamily, type MarketFamily } from '../lib/protocol';

/**
 * Подпись дисциплины для человека.
 *
 * `other:table_tennis` — служебный код: приставка `other:` говорит движку «в
 * матчинге не участвует» и не говорит человеку ничего. Таких строк у донора
 * около десятой части, и показывать их кодом значит объявить настольный теннис
 * технической ошибкой. Имя приходит с сервера полем `sportName`; оно и главнее.
 */
export { sportTitle } from '../lib/sports';

/** Живая лента или предматч. Неизвестное состояние — не повод врать про live. */
export const lineOf = (fork: Fork): string | null =>
  typeof fork.line === 'string' && fork.line !== '' ? fork.line : null;

/**
 * Проходит ли строка фильтры.
 *
 * Ни одно правило не режет по НЕИЗВЕСТНОМУ. Строка без подписи дисциплины при
 * выбранной дисциплине остаётся: спрятать её значило бы выдать пробел в данных
 * за ответ человека, и он даже не узнает, что вилку от него скрыли.
 */
/**
 * Режет ли вкус человека эту вилку у одной кассы.
 *
 * Без `ev` не режем: спрятать пробел в склейке за ответ человека — враньё.
 * Правило без рынка прячет весь матч у кассы; с рынком и стороной — только
 * этот исход. Прячем всю вилку: вторая нога без первой не ставится.
 */
export function excludedByAccount(
  fork: { ev?: string; market?: string; legs?: readonly { s: string; side: string }[] },
  accounts: Record<string, Pick<Account, 'exclude'>>,
): boolean {
  return exclusionHit(fork, accounts) !== null;
}

export function exclusionHit(
  fork: { ev?: string; market?: string; legs?: readonly { s: string; side: string }[] },
  accounts: Record<string, Pick<Account, 'exclude'>>,
): { venue: string; rule: VenueExclusion } | null {
  const ev = typeof fork.ev === 'string' ? fork.ev.trim() : '';
  if (ev === '') return null;
  const market = typeof fork.market === 'string' ? fork.market : '';
  for (const leg of fork.legs ?? []) {
    const venue = leg.s;
    if (!venue) continue;
    for (const rule of accounts[venue]?.exclude ?? []) {
      if (rule.ev !== ev) continue;
      if (!rule.market) return { venue, rule };
      if (rule.market !== market) continue;
      if (rule.side && rule.side !== leg.side) continue;
      return { venue, rule };
    }
  }
  return null;
}

function asBooks(venues: readonly string[]): string[] {
  return canonVenues(venues);
}

/** Главная и счета — два независимых условия, не три чтения одного списка. */
export function venuesPass(
  sources: readonly string[],
  unnamed: boolean,
  f: Pick<Filters, 'venues' | 'venueMode' | 'mainVenues'>,
): boolean {
  const mains = new Set(asBooks(f.mainVenues ?? []));
  if (mains.size > 0 && sources.length > 0 && !unnamed) {
    if (!sources.some((source) => mains.has(source))) return false;
  }
  const accounts = asBooks(f.venues).filter((code) => f.venueMode !== 'except' || !mains.has(code));
  if (accounts.length === 0) return true;
  const chosen = new Set(accounts);
  if (TRANSFERS) {
    const books = sources.filter((source) => source !== 'polymarket');
    if (f.venueMode === 'only') return books.every((source) => chosen.has(source));
    return !books.some((source) => chosen.has(source));
  }
  if (f.venueMode === 'only') {
    const allowed = new Set([...chosen, ...mains]);
    return sources.every((source) => allowed.has(source));
  }
  return !sources.some((source) => chosen.has(source));
}

export function passes(
  row: FeedRow,
  f: Filters,
  closed: ReadonlySet<string> = EMPTY,
  accounts: Record<string, Pick<Account, 'exclude'>> = {},
  locked: PageLine | null = null,
  opts?: { allowMirror?: boolean },
): boolean {
  if (PRIVATE_MIRROR && !cupisPairPass(row.row.legs.map(leg => leg.source), f.cupisPairMode, f.cupisPairs)) return false;
  if (PRIVATE_MIRROR && f.cupisFlow !== 'transfers' && row.row.roiPct < 0) return false;
  const fork = row.row.fork;
  // mir=5 — внутренний канал, не фишка человека. Строки exp: с ним
  // остаются, иначе вкладка фильтров не к чему применяться.
  if (!EXPERIMENTS && !opts?.allowMirror) {
    const raw = (fork as { mir?: unknown }).mir;
    const mir = typeof raw === 'number' ? raw : Number(raw);
    if (mir === 5 && !isLenta5Id(typeof fork.id === 'string' ? fork.id : '')) return false;
  }
  if (TRANSFERS && !transferEligible(row.row, transferFloor(f.roiMin ?? -5))) return false;
  if (TRANSFERS && !transferFiltersPass(row, f)) return false;

  // Контора, где счёта нет, — это не «менее интересная» строка, её нет вовсе.
  // Список приходит из `accounts.ts` и живёт отдельно от чёрного списка фишек
  // намеренно: фишку человек ставит на сегодня, а закрытый счёт — это факт,
  // который он отмечает один раз и забывает.
  if (closed.size > 0 && row.row.legs.some((leg) => closed.has(leg.source))) return false;

  // Матч или исход у одной кассы — тот же слой, что закрытый счёт: факт про
  // человека, не фишка сита на сегодня. held смотрит вызывающий.
  if (Object.keys(accounts).length > 0 && excludedByAccount(fork, accounts)) return false;

  const sources = row.row.legs.map((leg) => bookOf(leg.source) || leg.source);
  // Пустые имена и `hidden` не режем: спрятать вилку без названия значило бы
  // выдать пробел в данных за ответ человека (тот же закон, что у дисциплины).
  const unnamed = sources.some((source) => source === '' || source === UNNAMED_VENUE);
  if (!venuesPass(sources, unnamed, f)) return false;

  if (f.tapes.length > 0) {
    const tape = tapeOfFork(fork);
    if (!f.tapes.includes(tape)) return false;
  }

  if (f.sports.length > 0) {
    const code = sportOf(fork);
    const chosen = new Set(f.sports.map((one) => sportCanon(one)).filter(Boolean));
    if (code !== '' && !chosen.has(code)) return false;
  }

  // Семейство вычислимо всегда («прочее» — тоже имя), поэтому правило про
  // неизвестное здесь не нарушается: строка без рынка — это «прочее», и её
  // прячет только тот, кто «прочее» не выбрал.
  if (f.markets.length > 0 && !marketPasses(marketTags(fork), f.markets, f.marketMode ?? 'except')) return false;

  // Страница лайв/прематч — это не фишка сита: забытый `lines` в localStorage
  // не имеет права опустошить соседнюю страницу, а профиль «только прематч» —
  // страницу лайв. Неизвестная линия не режется: пробел в данных не ответ.
  if (locked !== null) {
    const line = lineOf(fork);
    if (line !== null && line !== locked) return false;
  } else if (f.lines.length > 0) {
    const line = lineOf(fork);
    if (line !== null && !f.lines.includes(line)) return false;
  }

  if (TRANSFERS) {
    if (f.roiMax !== null && transferRoi(row.row).gt(f.roiMax)) return false;
  } else {
    if (f.roiMin !== null && row.row.roiPct < f.roiMin) return false;
    if (f.roiMax !== null && row.row.roiPct > f.roiMax) return false;
  }
  // Прематч возраст не режет: рождение строки у зеркала — часы, не порча.
  // Лайв держит и сито, и жёсткий потолок четверти часа.
  if (lineOf(fork) !== 'prematch') {
    if (f.ageMax !== null && row.age > f.ageMax) return false;
    if (f.ageMin !== null && row.age < f.ageMin) return false;
    if (!TRANSFERS && !RU_FEED && row.age > 15 * 60) return false;
  }

  // «Только вход»: хотя бы одна нога — событие или исход. Секция и пустой URL
  // при жизни вилки 10–15 с — очередь в никуда, не товар.
  if (ENTRY_SIEVE && f.entryOnly) {
    const good = row.row.legs.some((leg) => entryOpen(leg));
    if (!good) return false;
  }


  return true;
}

/** Объём именно показанной PM-ноги, не признак касания общего потолка. */
export function transferBookCovers(row: FeedRow): boolean {
  const pm = row.row.legs.find((leg) => leg.source === 'polymarket');
  const book = row.row.fork.legs?.find((leg) => leg.s === 'polymarket')?.book;
  if (!pm || !book?.length || row.ghost !== null || row.row.doubt === 'dead' ||
      pm.ownOdds || pm.shares === null || pm.usd === null || pm.cents === null ||
      pm.shares <= 0 || pm.usd <= 0 || !Number.isFinite(pm.cents)) return false;
  try {
    // Проход по ask до числа шеров на экране. Итоговая стоимость должна
    // помещаться в показанный расход; одна метка cap этого не доказывает.
    let remaining = new Decimal(pm.shares);
    let cost = new Decimal(0);
    const limit = new Decimal(pm.cents).div(100);
    for (const [priceText, sizeText] of book) {
      const price = new Decimal(priceText);
      const size = new Decimal(sizeText);
      if (!price.isFinite() || !size.isFinite() || price.lte(0) || price.gte(1) || size.lt(0)) return false;
      if (price.gt(limit)) continue;
      const take = Decimal.min(remaining, size);
      cost = cost.plus(take.times(price));
      remaining = remaining.minus(take);
      if (remaining.isZero()) break;
    }
    return remaining.isZero() && cost.toDecimalPlaces(2, Decimal.ROUND_UP)
      .lte(new Decimal(pm.usd).toDecimalPlaces(2, Decimal.ROUND_UP));
  } catch {
    return false;
  }
}

/** Fresh/consensus/lead относятся к котировкам, не к расчёту исходов.
 * Пока сервер не передаёт положительное свидетельство settlement, строка
 * остаётся непроверенной. Пустые checks/rl никогда не заменяют свидетельство. */
export function transferRulesVerified(row: FeedRow): boolean {
  const checks = row.row.fork.checks;
  return checks?.ok.includes('settlement') === true &&
    !checks.unknown.includes('settlement') &&
    !(row.row.fork.rl ?? []).some((rule) => /rules|settlement|draw|overtime|period/.test(rule.c));
}

export function transferFullyExecutable(row: FeedRow): boolean {
  if (row.row.legs.length !== 2 || !transferBookCovers(row) || row.risky ||
      row.still !== null || row.row.doubt !== null || row.row.blockers.length > 0) return false;
  const book = row.row.legs.find((leg) => leg.source !== 'polymarket');
  const pm = row.row.legs.find((leg) => leg.source === 'polymarket');
  const pmWire = row.row.fork.legs?.find((leg) => leg.s === 'polymarket');
  if (!book || !pm || book.cap.kind !== 'limit' || book.cap.value === null ||
      !Number.isFinite(book.cap.value) || book.ownOdds || book.freebet ||
      !row.row.fork.checks?.ok.includes('fresh') || pmWire?.feeRate == null ||
      pm.shares === null || pm.minSize === null || !Number.isFinite(pm.minSize) ||
      pm.usd === null) return false;
  const amount = book.usd ?? book.rub;
  return new Decimal(amount).gt(0) && new Decimal(amount).lte(book.cap.value) &&
    new Decimal(pm.shares).gte(pm.minSize) && new Decimal(pm.usd).gte(5);
}

/** Проверки только для подборки; закреплённые ставки сохраняет sift. */
export function transferFiltersPass(row: FeedRow, f: Filters): boolean {
  return (!f.transferBook || transferBookCovers(row)) &&
    (!f.transferRules || transferRulesVerified(row)) &&
    (!f.transferFull || transferFullyExecutable(row));
}

function transferConvenience(row: FeedRow): number {
  return Number(transferFullyExecutable(row)) * 8 + Number(transferRulesVerified(row)) * 4 +
    Number(transferBookCovers(row)) * 2 + Number(entryRank(row) >= 0.85);
}

/**
 * Ранг точности входа худшей ноги. Исход = 1, матч = 0.85, раздел = 0.25,
 * нет адреса = 0.1. Строка 8% без URL хуже строки 1.2% с купоном.
 */
const ENTRY_RANK: Record<string, number> = {
  outcome: 1,
  event: 0.85,
  section: 0.25,
  venue: 0.25,
};

export function entryRank(row: FeedRow): number {
  const legs = row.row.legs;
  if (legs === undefined || legs.length === 0) return 1;
  let worst = 1;
  for (const leg of legs) {
    const rank = ENTRY_RANK[leg.precision] ?? 0.1;
    if (rank < worst) worst = rank;
  }
  return worst;
}

/**
 * Ожидаемые рубли за окно жизни: прибыль × шанс дожить × ранг входа.
 * Счёт уже режет `passes` (закрытый — строка не товар). Неизмеренная жизнь
 * не штрафуется: «мы не знаем» и «живёт ноль секунд» — разные утверждения.
 */
export function expectedProfit(row: FeedRow, tempo: number): number {
  const life = row.lifespan;
  const lived = life === null || !(tempo > 0) ? row.row.profit : row.row.profit * Math.min(1, life / tempo);
  return lived * entryRank(row);
}

/**
 * Сравнение строк для выбранного порядка.
 *
 * Умолчание — деньги, и это не вкусовщина. Замер 02.08: 27.69 % стоили 198 ₽
 * при стакане в шесть долларов, а 14.53 % — 1451 ₽ при глубине 472 $. Порядок
 * по проценту ставил наверх ровно то, что брать нельзя, и 23.08 снят с сита.
 *
 * В порядке «деньги» прибыль ещё умножается на min(1, sv/tempo): +900 ₽ с
 * жизнью 6 с уходит под +400 ₽ с жизнью 25 с. Это и есть «быстрые и проверенные»
 * без нового антискама.
 */
export function compare(a: FeedRow, b: FeedRow, sort: SortKey, tempo = 11): number {
  // Сомнительная строка тонет при ЛЮБОМ порядке, а не только в умолчании. Верх
  // ленты — это рекомендация, и рекомендовать вилку, в которую мы сами не
  // верим, значит проигрывать чужие деньги (07.08, владелец).
  const sink = distrust(a) - distrust(b);
  if (sink !== 0) return sink;
  // Последняя цена ниже свежих: её ещё видно, но верх ленты — то, что только
  // что пришло. Иначе застывшие 12% стоят над живыми 6%.
  const lag = Number(a.row.fork.lag === true) - Number(b.row.fork.lag === true);
  if (lag !== 0) return lag;
  switch (sort) {
    case 'age':
      return a.age - b.age;
    case 'life':
      // Неизмеренная живучесть уходит вниз, а не считается нулём: «мы не знаем»
      // и «живёт ноль секунд» — разные утверждения, и второе мы не вправе
      // делать за выборку, которой у нас нет.
      return (b.lifespan ?? -1) - (a.lifespan ?? -1);
    default:
      return TRANSFERS
        ? transferConvenience(b) - transferConvenience(a) || transferRoi(b.row).comparedTo(transferRoi(a.row))
        : expectedProfit(b, tempo) - expectedProfit(a, tempo);
  }
}

// ── Лента после сита ─────────────────────────────────────────────────────────

/** Сколько строк снял фильтр самого человека — сверх того, что вернул расчёт. */
export interface Sifted extends FeedView {
  hidden: number;
  /**
   * Сколько из скрытого снял профиль, а не быстрые фишки над лентой. Числа
   * разные, и разделять их обязательно: «фильтр скрыл сорок строк» при пустых
   * фишках выглядит враньём, пока не сказано, что фильтр — это профиль,
   * применённый в студии полчаса назад и с тех пор забытый.
   */
  hiddenByProfile: number;
  /** Сколько строк снял фильтр «свежесть от». */
  hiddenByFresh?: number;
  /**
   * Сколько строк снял ПОТОЛОК процента — свой или профильный.
   *
   * Отдельное число, потому что отдельная беда. Все прочие фильтры убирают то,
   * что человек и так не берёт: чужую контору, не свой спорт. Потолок процента
   * убирает САМОЕ ВИДНОЕ — верх ленты, и оставшиеся полпроцента выглядят не как
   * «отфильтровано», а как «рынок умер» или «сканер сломался». Мелкой серой
   * надписи «скрыто 40» для этого мало: её читают как техническую подробность.
   *
   * Замер этой ошибки живой: кнопка «мягкие» стояла рядом со звуком, и первое же
   * случайное попадание опустошало верх ленты молча. Кнопку убрали, а потолок
   * остался — его приносит профиль из студии и переживает перезагрузку вкладки.
   */
  hiddenByCeiling: number;
  /** Сам потолок, чтобы назвать его числом. `null` — потолка нет. */
  ceiling: number | null;
  /** Сколько снял бан матча/исхода у кассы. Отдельно: это не фишка сита. */
  hiddenByExclude: number;
}

/**
 * Просеять и упорядочить готовый кадр.
 *
 * Отдельный слой над расчётом, а не правка внутри него. Расчёт строки — это
 * арифметика вилки и ничего больше; кого из них показывать — знание о человеке.
 * Слить их в одну функцию значило бы, что кэш расчёта обесценивается каждым
 * нажатием на фишку конторы, хотя ни одно число в строках при этом не меняется.
 *
 * При `frozen` порядок не трогается вовсе: заморозку ставит рука на строке, и
 * сортировать поверх неё значило бы её отменить — список, переехавший за время
 * движения руки, это ставка не по той цене.
 *
 * Чужую линию страница отрезает до счёта «скрыто»: иначе пустой лайв при полном
 * прематче читался бы как «ваш фильтр скрыл N», хотя человек ничего не ставил.
 */
function lockView(view: FeedView, locked: PageLine | null): FeedView {
  if (locked === null) return view;
  const keep = (rows: readonly FeedRow[]): FeedRow[] =>
    rows.filter((row) => {
      // Held и полка не переносят лайв на вкладку прематч: карточка тогда
      // читается как прематч, хотя матч уже идёт (14.10). Неизвестная линия
      // по-прежнему не режется — пробел в данных не ответ.
      const line = lineOf(row.row.fork);
      return line === null || line === locked;
    });
  return { ...view, big: keep(view.big), tail: keep(view.tail) };
}

export function sift(
  view: FeedView,
  f: Filters,
  frozen = false,
  profile: Profile | null = null,
  closed: ReadonlySet<string> = EMPTY,
  held: ReadonlySet<string> = EMPTY,
  clipped: ReadonlySet<string> = EMPTY,
  accounts: Record<string, Pick<Account, 'exclude'>> = {},
  locked: PageLine | null = null,
): Sifted {
  const scoped = lockView(view, locked);
  // The legacy work profile excluded two RUB books. CUPIS explicitly
  // supports these pairs; pair selection above is the applicable control.
  if ((PRIVATE_MIRROR || RU_FEED) && profile?.curKind === 'mix') {
    profile = { ...profile, curKind: 'any' };
  }
  if (PRIVATE_MIRROR && f.cupisFlow === 'transfers' && profile) {
    profile = { ...profile, minRoi: f.roiMin ?? -3, maxRoi: f.roiMax ?? 3, minRub: -Infinity, perMin: 0 };
  }
  // Порядок из профиля действует, пока человек не тронул переключатель над
  // лентой: быстрый выбор глазами главнее настройки, сделанной час назад.
  const order = f.sort === EMPTY_FILTERS.sort && profile !== null ? profile.sort : f.sort;
  // Потолков может быть два — свой и профильный. Берём НИЖНИЙ: он и режет.
  // В Переливах верхний NET — нижняя стоимость, не арбитражный потолок.
  // Баннер «снять потолок 0%» не должен предлагать вернуть плюсовые вилки.
  const caps = (TRANSFERS ? [] : [f.roiMax, profile !== null && profile.maxRoi > 0 ? profile.maxRoi : null]).filter(
    (one): one is number => one !== null,
  );
  const ceiling = caps.length > 0 ? Math.min(...caps) : null;
  let byProfile = 0;
  let byCeiling = 0;
  let byExclude = 0;
  let byFresh = 0;
  const keep = (rows: readonly FeedRow[]): FeedRow[] => {
    const kept = rows.filter((row) => {
      // Вилка «в работе» не отсеивается ничем: человек, возможно, уже поставил
      // первую ногу, и спрятать строку фильтром значило бы забрать у него
      // числа, по которым он ставит вторую. Закреплённая — то же: её держат
      // как раз когда фильтр или процент уже хотели бы её спрятать.
      if (RU_FEED && (!ruPairOk(row.row.fork) || !Number.isFinite(row.row.roiPct) || row.row.roiPct < RU_MIN_ROI)) return false;
      if (held.has(row.id) || clipped.has(row.id)) return true;
      // Потолок считается ПЕРВЫМ и по самой строке, а не по причине отказа:
      // строка выше потолка обычно не проходит и что-нибудь ещё, и в общем
      // счётчике «скрыто» она потерялась бы среди чужих контор.
      if (ceiling !== null && row.row.roiPct > ceiling) byCeiling += 1;
      if (profile !== null && !holds(row, profile, locked)) {
        byProfile += 1;
        return false;
      }
      if (excludedByAccount(row.row.fork, accounts)) {
        byExclude += 1;
        return false;
      }
      if (f.ageMin !== null && lineOf(row.row.fork) !== 'prematch' && row.age < f.ageMin) {
        byFresh += 1;
        return false;
      }
      return passes(row, f, closed, accounts, locked, {
        allowMirror: isPreparedFeed2(row) || RU_FEED && isCupisForkId(row.id),
      });
    });
    // Взятая в работу строка стоит первой при любом порядке: к ней человек
    // будет возвращаться из вкладок контор, и искать её глазами по всей ленте —
    // это секунды, которых у вилки нет.
    if (frozen) return kept;
    // Порядок ставится не точным значением, а сеткой с памятью мест (`calm.ts`):
    // иначе две вилки, различающиеся на два рубля, меняются местами по
    // несколько раз в секунду и уводят строку из-под глаза.
    const ordinary = settle(
      kept.filter((row) => !isPreparedFeed2(row)),
      TRANSFERS && order === 'money' ? 'roi' : order,
      (a, b) =>
        Number(held.has(b.id)) - Number(held.has(a.id)) ||
        compare(a, b, order, profile?.tempo ?? 11),
      (row) => TRANSFERS && order === 'money'
        ? (held.has(row.id) ? 0 : 64) + distrust(row) * 16 - transferConvenience(row)
        : (held.has(row.id) ? 0 : 4) + distrust(row),
    );
    // Main sort/tempo settings may reorder ordinary rows, but must not change
    // feed2's relative order produced by the shared /tests row builder.
    let index = 0;
    return kept.map((row) => isPreparedFeed2(row) ? row : ordinary[index++]!);
  };
  let big = keep(scoped.big);
  let tail = keep(scoped.tail);
  if (PRIVATE_MIRROR && f.cupisFlow === 'transfers') {
    // Loss-making transfers belong in the visible list, not the profit tail.
    big = [...big, ...tail];
    tail = [];
  }

  // Два зеркала иногда привозят одну и ту же кассу на тот же матч. Без свёртки
  // человек ставит дважды по одной цене. На боевой ленте этого нет — там один
  // движок. Предикт-лента — свой движок и только пары двух бирж: ни свёртки
  // зеркал, ни сита пар (оно прячет «предикт × предикт») там быть не должно.
  // /tests пары отбирает сам: сито Кибер считает биржи и Orbitex чужими.
  if (MIRROR_FEED && !PREDICT_FEED) {
    const of = (row: FeedRow) => row.row.fork;
    // feed2 rows are already deduplicated by the source pipeline.  Running the
    // mirror twin reducer here would merge different periods/maps on the same
    // event and discard valid ready arbs.
    const keepOrdinary = (rows: FeedRow[]): FeedRow[] => {
      if (RU_FEED) return rows;
      const ordinary = new Set(keepTwin(rows.filter((row) => !isPreparedFeed2(row)), of));
      return rows.filter((row) => isPreparedFeed2(row) || ordinary.has(row));
    };
    big = keepOrdinary(big);
    tail = keepOrdinary(tail);
    if (!EXPERIMENTS) {
      const pairOk = RU_FEED ? ruPairOk : feedPairOk;
      big = big.filter((row) => isPreparedFeed2(row) || pairOk(of(row)));
      tail = tail.filter((row) => isPreparedFeed2(row) || pairOk(of(row)));
    }
  }

  // Свёртка по событию — последней, уже по просеянному. Тотал 22.5 и 23.5 на
  // одном матче это одна и та же вилка на соседних линиях: поставить обе нельзя
  // (вторая съест лимит первой), а глазами они читаются как две возможности.
  // Считать её раньше нельзя: победитель, выбранный до сита, мог бы сам не
  // пройти профиль — и тогда свёртка убрала бы и его, и всех, кого он закрыл.
  if (profile !== null && profile.collapse) {
    const best = new Map<string, number>();
    for (const row of [...big, ...tail]) {
      if (isPreparedFeed2(row)) continue;
      const key = keyOf(row);
      const seen = best.get(key);
      const score = TRANSFERS ? row.row.roiPct : row.row.profit;
      if (seen === undefined || score > seen) best.set(key, score);
    }
    const winners = new Set<string>();
    const survives = (row: FeedRow): boolean => {
      if (isPreparedFeed2(row)) return true;
      const key = keyOf(row);
      // Держим по одной строке на событие. Ключ считает сервер (`ev`), у строк
      // без него ключ свой — такая строка не сворачивается ни с кем.
      if (held.has(row.id) || clipped.has(row.id)) return true;
      if (winners.has(key)) return false;
      const score = TRANSFERS ? row.row.roiPct : row.row.profit;
      if (score < (best.get(key) ?? 0)) return false;
      winners.add(key);
      return true;
    };
    const foldedBig = big.filter(survives);
    const foldedTail = tail.filter(survives);
    byProfile += big.length - foldedBig.length + (tail.length - foldedTail.length);
    big = foldedBig;
    tail = foldedTail;
  }

  // Места запоминаются по итоговому кадру — тому, что увидит человек. Делать
  // это внутри сортировки нельзя: там список ещё не прошёл свёртку повторов, и
  // строка, снятая свёрткой, оставила бы за собой занятое место.
  rememberPlaces([...big, ...tail]);

  return {
    ...view,
    big,
    tail,
    hidden: scoped.big.length + scoped.tail.length - big.length - tail.length,
    hiddenByProfile: byProfile,
    hiddenByCeiling: byCeiling,
    ceiling,
    hiddenByExclude: byExclude,
    hiddenByFresh: byFresh,
  };
}

/** Лента, какой её видит человек: посчитана, просеяна и упорядочена. */
export function useSifted(): Sifted {
  const view = useRows();
  const frozen = useScreen((s) => s.frozen);
  const steps = useScreen((s) => s.steps);
  const open = useScreen((s) => s.open);
  const popped = useScreen((s) => s.popped);
  const hiddenForks = useScreen((s) => s.hiddenForks);
  const held = useMemo(
    () => heldIds({ steps, open, popped, hiddenForks }),
    [steps, open, popped, hiddenForks],
  );
  const clipList = useClips();
  const clipped = useMemo(() => new Set(clipList), [clipList]);
  const profile = useProfile((s) => s.profile);
  useClipPrune();
  const venues = useFilters((s) => s.venues);
  const venueMode = useFilters((s) => s.venueMode);
  const mainVenues = useFilters((s) => s.mainVenues);
  const sports = useFilters((s) => s.sports);
  const markets = useFilters((s) => s.markets);
  const marketMode = useFilters((s) => s.marketMode);
  const lines = useFilters((s) => s.lines);
  const tapes = useFilters((s) => s.tapes);
  const cupisFlow = useFilters(s => s.cupisFlow);
  const cupisPairMode = useFilters(s => s.cupisPairMode);
  const cupisPairs = useFilters(s => s.cupisPairs);
  const roiMin = useFilters((s) => s.roiMin);
  const roiMax = useFilters((s) => s.roiMax);
  const ageMax = useFilters((s) => s.ageMax);
  const ageMin = useFilters((s) => s.ageMin);
  const sort = useFilters((s) => s.sort);
  const entryOnly = useFilters((s) => s.entryOnly);
  const transferFull = useFilters((s) => s.transferFull);
  const transferRules = useFilters((s) => s.transferRules);
  const transferBook = useFilters((s) => s.transferBook);
  const accounts = useAccounts((s) => s.accounts);
  const forks = useScreen((s) => s.forks);
  const touchExclusions = useAccounts((s) => s.touchExclusions);

  // Набор пересобирается только при смене самих счетов: он попадает в зависимости
  // сита, и новый `Set` на каждую перерисовку просеивал бы ленту постоянно.
  const closed = useMemo(
    () =>
      new Set(
        Object.entries(accounts)
          .filter(([, one]) => one.standing === 'closed')
          .map(([venue]) => venue),
      ),
    [accounts],
  );

  // Поля разбираются по одному, а не берутся объектом: `useFilters()` отдаёт
  // новый объект на каждую перерисовку, и мемоизация по нему не работала бы
  // вовсе — лента просеивалась бы на каждое движение мыши.
  const locked = pageLine(location.pathname);
  const dry = isDry(location.pathname);
  const out = useMemo(
    () =>
      sift(
        view,
        dry
          ? EMPTY_FILTERS
          : { ...EMPTY_FILTERS, venues, venueMode, mainVenues, sports, markets, marketMode, lines, tapes, cupisFlow, cupisPairMode, cupisPairs, roiMin, roiMax, ageMax, ageMin, sort, entryOnly, transferFull, transferRules, transferBook },
        frozen,
        dry ? null : profile,
        dry ? new Set() : closed,
        held,
        clipped,
        dry ? {} : accounts,
        dry ? null : locked,
      ),
    [view, frozen, profile, venues, venueMode, mainVenues, sports, markets, marketMode, lines, tapes, cupisFlow, cupisPairMode, cupisPairs, roiMin, roiMax, ageMax, ageMin, sort, entryOnly, transferFull, transferRules, transferBook, closed, held, clipped, accounts, locked, dry],
  );

  // Цена профиля уезжает в его же хранилище, чтобы шапка назвала её, не считая
  // ленту второй раз. Запись идёт эффектом: менять чужой стор во время
  // отрисовки — способ получить бесконечную перерисовку на ровном месте.
  const noteHidden = useProfile((s) => s.noteHidden);
  useEffect(() => noteHidden(out.hiddenByProfile), [noteHidden, out.hiddenByProfile]);

  // Автосъём считается по проводу, не по просеянной ленте: спрятанный матч
  // всё ещё жив, и часы не должны тикать, пока пара есть в кадре.
  useEffect(() => {
    const live = new Set<string>();
    for (const fork of forks.values()) {
      const ev = typeof fork.ev === 'string' ? fork.ev.trim() : '';
      if (ev === '') continue;
      for (const leg of fork.legs ?? []) {
        if (leg.s) live.add(`${leg.s}\t${ev}`);
      }
    }
    touchExclusions(live);
  }, [forks, touchExclusions]);

  return out;
}

// ── Хранилище ────────────────────────────────────────────────────────────────

const FILTERS_VER = RU_FEED ? 'forq.next.filters.ru-seven-v1' : TRANSFERS ? 'forq.next.filters.wide-1010' : 'forq.next.filters.v2';
const SHARED_KEY = feedKey(FILTERS_VER);

export function filtersStorageKey(): string {
  return SHARED_KEY;
}

const KEY = filtersStorageKey();

function unionList(left: unknown, right: unknown): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [...(list(left) ?? []), ...(list(right) ?? [])]) {
    if (seen.has(raw)) continue;
    seen.add(raw);
    out.push(raw);
  }
  return out;
}

function mergeSieve(live: Partial<Filters>, prematch: Partial<Filters>): Partial<Filters> {
  return {
    ...prematch,
    ...live,
    sports: unionList(live.sports, prematch.sports),
    venues: unionList(live.venues, prematch.venues),
    markets: unionList(live.markets, prematch.markets),
    mainVenues: unionList(live.mainVenues, prematch.mainVenues),
    tapes: canonTapes(unionList(live.tapes, prematch.tapes)),
    venueMode: live.venueMode ?? prematch.venueMode,
    marketMode: live.marketMode ?? prematch.marketMode,
  };
}

export function loadStoredFilters(): Partial<Filters> {
  return load();
}

function load(): Partial<Filters> {
  try {
    const raw = localStorage.getItem(SHARED_KEY);
    if (raw !== null) return JSON.parse(raw) as Partial<Filters>;
    const liveRaw = localStorage.getItem(feedKey(`${FILTERS_VER}.live`));
    const preRaw = localStorage.getItem(feedKey(`${FILTERS_VER}.prematch`));
    const live = liveRaw !== null ? (JSON.parse(liveRaw) as Partial<Filters>) : {};
    const prematch = preRaw !== null ? (JSON.parse(preRaw) as Partial<Filters>) : {};
    if (liveRaw === null && preRaw === null) return {};
    return mergeSieve(live, prematch);
  } catch {
    // Испорченная запись не имеет права мешать открыть сканер.
    return {};
  }
}

const list = (value: unknown): string[] | null =>
  Array.isArray(value) ? value.filter((one): one is string => typeof one === 'string') : null;

const bound = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function roiBounds(min: number | null, max: number | null): Pick<Filters, 'roiMin' | 'roiMax'> {
  if (!TRANSFERS) return { roiMin: min, roiMax: max };
  const roiMin = transferFloor(min);
  const roiMax = transferFloor(max ?? 1);
  return { roiMin, roiMax: Math.max(roiMin, roiMax) };
}

const sourceBooks = (venues: string[]): string[] => {
  const next = TRANSFERS
    ? venues.filter((venue) => !['polymarket', 'hidden', 'kalshi', 'predictfun', 'sxbet', 'betfair', 'orbitex'].includes(venue)).slice(0, 1)
    : venues;
  if (!EXPERIMENTS) return next;
  return next.filter((venue) => isMirrorBook(venue));
};

const asMain = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' && value !== 'hidden' ? value : null;

const asVenueMode = (value: unknown): VenueMode => (value === 'only' ? 'only' : 'except');

/** Одна касса — один код: `onewin` и `1win` в фильтре не живут рядом. */
export function canonVenues(venues: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of venues) {
    const code = bookOf(raw) || raw.trim();
    if (code === '' || seen.has(code) || sieveDropsBook(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

/**
 * Прочитанный набор контор. Старая вкладка держала главную режимом списка
 * (`venueMode: 'main'` или ещё старше — `mainVenue`). Теперь это отдельное
 * поле: иначе «главная» затирает белый и чёрный список.
 */
export function adoptVenueSieve(saved: {
  venues?: unknown;
  venueMode?: unknown;
  mainVenue?: unknown;
  mainVenues?: unknown;
  parkedMain?: unknown;
}): { venues: string[]; venueMode: VenueMode; mainVenues: string[] } {
  const venues = canonVenues(list(saved.venues) ?? []);
  const written = canonVenues(list(saved.mainVenues) ?? []);
  const old = asMain(saved.mainVenue);
  const settled = (next: { venues: string[]; venueMode: VenueMode; mainVenues: string[] }) => {
    if (next.venueMode !== 'except' || next.mainVenues.length === 0) return next;
    const mains = new Set(next.mainVenues);
    return { ...next, venues: next.venues.filter((code) => !mains.has(code)) };
  };
  if (written.length > 0) {
    return settled({ venues, venueMode: asVenueMode(saved.venueMode), mainVenues: written });
  }
  if (old !== null) {
    const main = bookOf(old) || old;
    if (TRANSFERS) {
      return { venueMode: 'only', venues: venues.includes(main) ? venues : [...venues, main], mainVenues: [] };
    }
    return settled({
      venueMode: asVenueMode(saved.venueMode),
      venues: venues.filter((code) => code !== main),
      mainVenues: [main],
    });
  }
  if (saved.venueMode === 'main') {
    // Переливы главной не знают: старый сейв — это отметка БК, не «на ноге».
    if (TRANSFERS) return { venueMode: 'only', venues, mainVenues: [] };
    return { venueMode: 'except', venues: [], mainVenues: venues };
  }
  return settled({ venues, venueMode: asVenueMode(saved.venueMode), mainVenues: [] });
}

export interface FiltersState extends Filters {
  toggleVenue: (source: string) => void;
  toggleMainVenue: (source: string) => void;
  setVenueMode: (mode: VenueMode) => void;
  toggleSport: (code: string) => void;
  toggleMarket: (code: string) => void;
  setMarketMode: (mode: MarketMode) => void;
  toggleLine: (line: string) => void;
  toggleTape: (code: string) => void;
  setRoi: (min: number | null, max: number | null) => void;
  setAgeMax: (seconds: number | null) => void;
  setAgeMin: (seconds: number | null) => void;
  setSort: (sort: SortKey) => void;
  setOpen: (open: boolean) => void;
  setEntryOnly: (on: boolean) => void;
  setTransfer: (key: 'transferFull' | 'transferRules' | 'transferBook', on: boolean) => void;
  /** Снять всё разом. Одна кнопка: выбираться из своих же фильтров человек не обязан. */
  clear: () => void;
  /** Принять готовый набор — этим студия применяет профиль к ленте. */
  apply: (next: Omit<Partial<Filters>, 'venueMode'> & { venueMode?: unknown; mainVenue?: unknown }) => void;
}

const saved = load();

const only = (f: FiltersState): Filters => ({
  ...((PRIVATE_MIRROR || RU_FEED) ? { cupisFlow: f.cupisFlow, cupisPairMode: f.cupisPairMode, cupisPairs: f.cupisPairs } : {}),
  venues: f.venues,
  venueMode: f.venueMode,
  mainVenues: f.mainVenues,
  sports: f.sports,
  markets: f.markets,
  marketMode: f.marketMode,
  lines: f.lines,
  tapes: f.tapes,
  roiMin: f.roiMin,
  roiMax: f.roiMax,
  ageMax: f.ageMax,
  ageMin: f.ageMin,
  sort: f.sort,
  open: f.open,
  entryOnly: f.entryOnly,
  transferFull: f.transferFull,
  transferRules: f.transferRules,
  transferBook: f.transferBook,
});

export const useFilters = create<FiltersState>((set, get) => {
  const store = (): void => {
    try {
      localStorage.setItem(KEY, JSON.stringify(only(get())));
    } catch {
      // Приватный режим и переполненное хранилище — не повод падать.
    }
  };
  const toggled = (current: string[], value: string): string[] =>
    current.includes(value) ? current.filter((one) => one !== value) : [...current, value];

  const adopted = adoptVenueSieve(saved);
  return {
    ...EMPTY_FILTERS,
    cupisFlow: (PRIVATE_MIRROR || RU_FEED) && saved.cupisFlow === 'transfers' ? 'transfers' : RU_FEED && saved.cupisFlow !== 'arbs' ? 'all' : 'arbs',
    cupisPairMode: (PRIVATE_MIRROR || RU_FEED) && ['all', 'russian', 'poly', 'selected'].includes(String(saved.cupisPairMode)) ? saved.cupisPairMode as CupisPairMode : 'all',
    cupisPairs: (PRIVATE_MIRROR || RU_FEED) ? list(saved.cupisPairs) ?? [] : [],
    venues: sourceBooks(TRANSFERS && adopted.venueMode === 'except' ? [] : adopted.venues),
    venueMode: TRANSFERS ? 'only' : adopted.venueMode,
    mainVenues: TRANSFERS ? [] : adopted.mainVenues,
    sports: list(saved.sports) ?? [],
    markets: migrateMarkets(list(saved.markets) ?? []),
    // До 27.09 отметки рынков значили «только эти»: без записанного режима их смысл сохраняем.
    marketMode: saved.marketMode === 'except' ? 'except' : saved.marketMode === 'only' || (list(saved.markets) ?? []).length > 0 ? 'only' : 'except',
    lines: list(saved.lines) ?? [],
    tapes: canonTapes(list(saved.tapes) ?? []),
    ...roiBounds(bound(saved.roiMin), bound(saved.roiMax)),
    ageMax: bound(saved.ageMax),
    ageMin: bound(saved.ageMin),
    sort: asSort(saved.sort),
    open: saved.open === true,
    entryOnly: ENTRY_SIEVE ? entryOnlyOnStart(saved) : false,
    transferFull: TRANSFERS ? saved.transferFull === true : false,
    transferRules: TRANSFERS ? saved.transferRules === true : false,
    transferBook: TRANSFERS ? saved.transferBook === true : false,

    toggleVenue: (source) => {
      if (TRANSFERS && source === 'polymarket') return;
      const canon = bookOf(source) || source;
      const cur = get();
      const next = TRANSFERS
        ? sourceBooks(cur.venues.includes(canon) ? [] : [canon])
        : toggled(canonVenues(cur.venues), canon);
      const adding = next.includes(canon);
      set({
        venues: next,
        mainVenues:
          !TRANSFERS && adding && cur.venueMode === 'except'
            ? cur.mainVenues.filter((code) => code !== canon)
            : cur.mainVenues,
      });
      store();
    },
    toggleMainVenue: (source) => {
      if (TRANSFERS) return;
      const canon = bookOf(source) || source;
      const cur = get();
      const next = toggled(canonVenues(cur.mainVenues), canon);
      const adding = next.includes(canon);
      set({
        mainVenues: next,
        venues:
          adding && cur.venueMode === 'except'
            ? cur.venues.filter((code) => code !== canon)
            : cur.venues,
      });
      store();
    },
    setVenueMode: (venueMode) => {
      set({ venueMode: TRANSFERS ? 'only' : venueMode === 'only' ? 'only' : 'except' });
      store();
    },
    toggleSport: (code) => {
      set({ sports: toggled(get().sports, code) });
      store();
    },
    toggleMarket: (code) => {
      set({ markets: toggled(get().markets, code) });
      store();
    },
    setMarketMode: (marketMode) => {
      set({ marketMode });
      store();
    },
    toggleLine: (line) => {
      set({ lines: toggled(get().lines, line) });
      store();
    },
    toggleTape: (code) => {
      set({ tapes: canonTapes(toggled(get().tapes, code)) });
      store();
    },
    setRoi: (roiMin, roiMax) => {
      set(roiBounds(roiMin, roiMax));
      store();
      applyProfilePatch(profilePatchFromSieve({ roiMin: get().roiMin, roiMax: get().roiMax }));
    },
    setAgeMax: (ageMax) => {
      set({ ageMax });
      store();
      applyProfilePatch(profilePatchFromSieve({ ageMax }));
    },
    setAgeMin: (ageMin) => {
      set({ ageMin });
      store();
    },
    setSort: (sort) => {
      set({ sort });
      store();
    },
    setOpen: (open) => {
      set({ open });
      store();
    },
    setEntryOnly: (entryOnly) => {
      set({ entryOnly: ENTRY_SIEVE ? entryOnly : false });
      store();
    },
    setTransfer: (key, on) => {
      set({ [key]: on } as Partial<FiltersState>);
      store();
    },
    clear: () => {
      // Тумблер входа — не «фильтр на сегодня», а честность ленты. Сброс
      // снимает конторы и полосу процента, не возвращает музей.
      set({
        ...EMPTY_FILTERS,
        venueMode: TRANSFERS ? 'only' : EMPTY_FILTERS.venueMode,
        ...roiBounds(null, null),
        open: get().open,
        sort: get().sort,
        entryOnly: ENTRY_SIEVE ? get().entryOnly : false,
        transferFull: TRANSFERS ? false : get().transferFull,
        transferRules: TRANSFERS ? false : get().transferRules,
        transferBook: TRANSFERS ? false : get().transferBook,
      });
      store();
    },
    apply: (next) => {
      const adopted = adoptVenueSieve({
        venues: 'venues' in next ? next.venues : get().venues,
        venueMode: 'venueMode' in next ? next.venueMode : get().venueMode,
        mainVenues: 'mainVenues' in next ? next.mainVenues : get().mainVenues,
        mainVenue: 'mainVenue' in next ? (next as { mainVenue?: unknown }).mainVenue : null,
      });
      set({
        ...((PRIVATE_MIRROR || RU_FEED) ? { cupisFlow: next.cupisFlow ?? get().cupisFlow, cupisPairMode: next.cupisPairMode ?? get().cupisPairMode, cupisPairs: next.cupisPairs ?? get().cupisPairs } : {}),
        venues: sourceBooks(TRANSFERS && adopted.venueMode === 'except' ? [] : adopted.venues),
        venueMode: TRANSFERS ? 'only' : adopted.venueMode,
        mainVenues: TRANSFERS ? [] : adopted.mainVenues,
        sports: list(next.sports) ?? get().sports,
        markets: list(next.markets) ?? get().markets,
        marketMode: next.marketMode === undefined ? get().marketMode : next.marketMode === 'only' ? 'only' : 'except',
        lines: list(next.lines) ?? get().lines,
        tapes: next.tapes === undefined ? get().tapes : canonTapes(list(next.tapes) ?? []),
        ...roiBounds(
          next.roiMin === undefined ? get().roiMin : bound(next.roiMin),
          next.roiMax === undefined ? get().roiMax : bound(next.roiMax),
        ),
        ageMax: next.ageMax === undefined ? get().ageMax : bound(next.ageMax),
        ageMin: next.ageMin === undefined ? get().ageMin : bound(next.ageMin),
        sort: next.sort === undefined ? get().sort : asSort(next.sort),
        entryOnly: ENTRY_SIEVE
          ? next.entryOnly === undefined ? get().entryOnly : next.entryOnly === true
          : false,
        transferFull: next.transferFull === undefined ? get().transferFull : next.transferFull === true,
        transferRules: next.transferRules === undefined ? get().transferRules : next.transferRules === true,
        transferBook: next.transferBook === undefined ? get().transferBook : next.transferBook === true,
      });
      store();
    },
  };
});
